import { useEffect, useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import RedoIcon from '@mui/icons-material/Redo';
import UpdateIcon from '@mui/icons-material/Update';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Divider, Grid, Typography } from '@mui/material';
import Button from '@mui/material/Button';

import { OrderStatus } from '../../constants/OrderStatus';
import { gray } from '../../constants/colors';
import { DocGenerationSource } from '../../constants/docGenerationSource';
import { OrderTypes } from '../../constants/orderTypeConstants';
import { IOptionalItems } from '../../type';

interface IProps {
  order: any;
  isIdentifyWitnessPartyByNotary?: boolean;
  isCurrentUserNotaryOnOrder?: boolean;
  isCurrentUserEscrowOfficerOnOrder: boolean;
  isCurrentUserEscrowOnOrder: boolean;
  isInkSignedDocsUploadedToVault?: boolean;
  havePendingNotary?: boolean;
  docsTagged: boolean;
  isThirdPartyOrder: boolean;
  docsEdited: boolean;
  notaryAssigned?: boolean;
  CancelableOrder: boolean;
  orderStatus: string;
  ipenSession?: any;
  handleClickResendLink?: (e: string) => void;
  navigateToProfile?: () => void;
  setOpenUploadDocs?: (v: boolean) => void;
  setOpenTagDocsModal: (v: boolean) => void;
  setOpenAssignNotary?: (v: boolean) => void;
  setOpenAddCoSa?: (v: boolean) => void;
  setShowCancelConfirmation: (v: boolean) => void;
  setOpenWitnessInfoModal?: (v: boolean) => void;
}

function OptionalActions(props: IProps) {
  const {
    order,
    isIdentifyWitnessPartyByNotary,
    isCurrentUserNotaryOnOrder,
    isCurrentUserEscrowOfficerOnOrder,
    isCurrentUserEscrowOnOrder,
    isInkSignedDocsUploadedToVault,
    havePendingNotary,
    isThirdPartyOrder,
    docsTagged,
    docsEdited,
    orderStatus,
    notaryAssigned,
    CancelableOrder,
    ipenSession,
    setOpenTagDocsModal,
    navigateToProfile,
    handleClickResendLink,
    setShowCancelConfirmation,
    setOpenAssignNotary,
    setOpenAddCoSa,
    setOpenUploadDocs,
    setOpenWitnessInfoModal,
  } = props;

  const [optionalItemsToShow, setOptionalItemsToShow] = useState<
    IOptionalItems[]
  >([]);
  const orderStatusIsOpenLinkSent: boolean =
    order.Status === OrderStatus.OpenLinksSent;

  useEffect(() => {
    const newOptionalItems: IOptionalItems[] = optionalItemsToShow;

    const addAssistantText =
      order?.OrderType?.DocGen === DocGenerationSource.FA
        ? 'Add Escrow Assistant'
        : 'Add Co-Settlement Agent';
    if (orderStatusIsOpenLinkSent && isCurrentUserEscrowOfficerOnOrder) {
      if (
        !newOptionalItems.find((n) => n.name === addAssistantText) &&
        setOpenAddCoSa
      ) {
        newOptionalItems.unshift({
          name: addAssistantText,
          onActionClick: () => {
            setOpenAddCoSa(true);
          },
          icon: <PersonAddAlt1Icon />,
        });
      }
    } else if (
      orderStatus === OrderStatus.OpenAllSigned ||
      !isCurrentUserEscrowOfficerOnOrder
    ) {
      if (newOptionalItems.find((n) => n.name === addAssistantText)) {
        const id = newOptionalItems.findIndex(
          (n) => n.name === addAssistantText
        );
        newOptionalItems.splice(id, 1);
      }
    }

    if (isCurrentUserNotaryOnOrder) {
      if (!newOptionalItems.find((n) => n.name === 'Edit Your Profile')) {
        newOptionalItems.unshift({
          name: 'Edit Your Profile',
          onActionClick: () => {
            if (navigateToProfile) {
              navigateToProfile();
            }
          },
          icon: <EditIcon />,
        });
      }
    } else {
      if (newOptionalItems.find((n) => n.name === 'Edit Your Profile')) {
        const id = newOptionalItems.findIndex(
          (n) => n.name === 'Edit Your Profile'
        );
        newOptionalItems.splice(id, 1);
      }
    }

    if (
      docsTagged &&
      docsEdited &&
      orderStatusIsOpenLinkSent &&
      isCurrentUserEscrowOnOrder
    ) {
      if (
        !newOptionalItems.find((n) => n.name === 'Upload Additional Documents')
      ) {
        newOptionalItems.unshift({
          name: 'Upload Additional Documents',
          onActionClick: () => {
            setOpenTagDocsModal(true);
          },
          icon: <FileUploadIcon />,
        });
      }
    } else if (
      orderStatus === OrderStatus.OpenAllSigned ||
      !isCurrentUserEscrowOnOrder
    ) {
      if (
        newOptionalItems.find((n) => n.name === 'Upload Additional Documents')
      ) {
        const id = newOptionalItems.findIndex(
          (n) => n.name === 'Upload Additional Documents'
        );
        newOptionalItems.splice(id, 1);
      }
    }

    if (
      isCurrentUserEscrowOfficerOnOrder &&
      !isThirdPartyOrder &&
      CancelableOrder &&
      !(
        orderStatus === OrderStatus.OpenAllSigned ||
        orderStatus?.includes(OrderStatus.Canceled)
      )
    ) {
      if (!newOptionalItems.find((n) => n.name === 'Cancel Order')) {
        newOptionalItems.push({
          name: 'Cancel Order',
          onActionClick: () => {
            setShowCancelConfirmation(true);
          },
          icon: <CancelIcon />,
        });
      }
    } else {
      if (newOptionalItems.find((n) => n.name === 'Cancel Order')) {
        const id = newOptionalItems.findIndex((n) => n.name === 'Cancel Order');
        newOptionalItems.splice(id, 1);
      }
    }

    if (order.OrderType.Type === OrderTypes.IPEN) {
      if (
        havePendingNotary &&
        orderStatusIsOpenLinkSent &&
        isCurrentUserEscrowOnOrder
      ) {
        if (
          !newOptionalItems.find((n) => n.name === 'Resend Registration Email')
        ) {
          newOptionalItems.unshift({
            name: 'Resend Registration Email',
            onActionClick: () => {
              if (handleClickResendLink) {
                handleClickResendLink(order?.OrderDetails?.IPEN?.Notary.Email);
              }
            },
            icon: <RedoIcon />,
          });
        }
      } else if (
        !havePendingNotary ||
        !orderStatusIsOpenLinkSent ||
        !isCurrentUserEscrowOnOrder
      ) {
        if (
          newOptionalItems.find((n) => n.name === 'Resend Registration Email')
        ) {
          const id = newOptionalItems.findIndex(
            (n) => n.name === 'Resend Registration Email'
          );
          newOptionalItems.splice(id, 1);
        }
      }

      if (
        isIdentifyWitnessPartyByNotary &&
        orderStatusIsOpenLinkSent &&
        isCurrentUserNotaryOnOrder
      ) {
        if (
          !newOptionalItems.find(
            (n) => n.name === 'Update Witness Information'
          ) &&
          isCurrentUserNotaryOnOrder &&
          setOpenWitnessInfoModal
        ) {
          newOptionalItems.unshift({
            name: 'Update Witness Information',
            onActionClick: () => {
              setOpenWitnessInfoModal(true);
            },
            icon: <UpdateIcon />,
          });
        }
      } else if (
        !isCurrentUserNotaryOnOrder ||
        !isIdentifyWitnessPartyByNotary ||
        !orderStatusIsOpenLinkSent
      ) {
        if (
          newOptionalItems.find((n) => n.name === 'Update Witness Information')
        ) {
          const id = newOptionalItems.findIndex(
            (n) => n.name === 'Update Witness Information'
          );
          newOptionalItems.splice(id, 1);
        }
      }

      if (
        !orderStatusIsOpenLinkSent ||
        ipenSession?.isActiveSession ||
        ipenSession?.isSessionComplete ||
        !isCurrentUserEscrowOnOrder
      ) {
        if (newOptionalItems.find((n) => n.name === 'Change Notary')) {
          const id = newOptionalItems.findIndex(
            (n) => n.name === 'Change Notary'
          );
          newOptionalItems.splice(id, 1);
        }
      } else if (
        orderStatusIsOpenLinkSent &&
        notaryAssigned &&
        isCurrentUserEscrowOnOrder
      ) {
        if (
          !newOptionalItems.find((n) => n.name === 'Change Notary') &&
          setOpenAssignNotary
        ) {
          newOptionalItems.unshift({
            name: 'Change Notary',
            onActionClick: () => setOpenAssignNotary(true),
            icon: <FindReplaceIcon />,
          });
        }
      }
    } else if (order.OrderType.Type === OrderTypes.HybridEClose) {
      if (isInkSignedDocsUploadedToVault) {
        if (
          !newOptionalItems.find(
            (n) => n.name === 'Upload Additional Ink Signed Docs to Vault'
          )
        ) {
          newOptionalItems.unshift({
            name: 'Upload Additional Ink Signed Docs to Vault',
            onActionClick: () => {
              if (setOpenUploadDocs) {
                setOpenUploadDocs(true);
              }
            },
            icon: <UploadFileIcon />,
          });
        }
      }
    }
    setOptionalItemsToShow(newOptionalItems);
  }, [
    isIdentifyWitnessPartyByNotary,
    orderStatus,
    ipenSession,
    isCurrentUserNotaryOnOrder,
    havePendingNotary,
    order,
    docsTagged,
    docsEdited,
    notaryAssigned,
  ]);

  return (
    <>
      {optionalItemsToShow.length > 0 && (
        <Box sx={{ backgroundColor: 'white' }}>
          <Grid item xs={12} sx={{ marginTop: 2 }}>
            <Divider />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}>
            <Typography
              sx={{
                color: gray,
                fontSize: '13px',
                position: 'absolute',
                backgroundColor: 'white',
                padding: 2,
              }}>
              OPTIONAL ACTIONS
            </Typography>
          </Grid>
          <Grid
            container
            spacing={0}
            justifyContent='center'
            sx={{ padding: 2, alignContent: 'center' }}>
            {optionalItemsToShow.map((actionItem, i) => (
              <Grid
                key={i}
                item
                xs={12}
                sm={4}
                xl={optionalItemsToShow.length > 4 ? 2 : 2.8}
                lg={optionalItemsToShow.length > 4 ? 2.4 : 2.8}
                md={4}
                sx={{ textAlign: 'center' }}>
                <Button
                  variant='text'
                  color='primary'
                  sx={{ textAlign: 'left' }}
                  startIcon={
                    optionalItemsToShow[i]?.icon ?? optionalItemsToShow[i].icon
                  }
                  onClick={optionalItemsToShow[i].onActionClick}>
                  {actionItem.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );
}

export default OptionalActions;
